"use client";

import { PhotoFallback } from "@/components/ui/PhotoFallback";
import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Quote, Star } from "lucide-react";
import type { Testimonial } from "@/lib/types";
import { portrait } from "@/lib/images";
import { HReveal } from "@/components/home-ui/HReveal";
import { H9VoiceVideos } from "@/components/home9/H9VoiceVideos";

const PER_PAGE = 3;

/** Learner voices - auto-rotating quote; the selector shows only 3 learners at a time and slides up to the next 3. */
export function H9Voices({ testimonials, programCount }: { testimonials: Testimonial[]; programCount: number }) {
  const N = testimonials.length;
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const t = testimonials[i % Math.max(1, N)];

  useEffect(() => {
    if (paused || N === 0) return;
    const id = setInterval(() => setI((v) => (v + 1) % N), 5000);
    return () => clearInterval(id);
  }, [paused, N]);

  if (!t) return null;

  // endless loop: the selector always shows the active learner followed by the next ones, wrapping past the end
  const visible = Array.from({ length: Math.min(PER_PAGE, N) }, (_, j) => testimonials[(i + j) % N]);

  return (
    <section className="he-sec he-hair">
      <div className="he-wrap">
        <HReveal className="flex flex-col justify-between gap-5 sm:flex-row sm:items-end">
          <div>
            <span className="he-kicker">Learner voices</span>
            <h2 className="he-title mt-3 text-3xl leading-tight sm:text-[2.6rem]">What they say after finishing</h2>
          </div>
        </HReveal>

        <div
          className="mt-10 grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1.45fr)_minmax(0,.55fr)]"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          {/* active quote */}
          <div className="he-card relative p-7 sm:p-9">
            <Quote className="h-9 w-9" style={{ color: "var(--he-accent)" }} />
            <div key={t.id} className="fade-up-in">
                <blockquote className="he-title mt-5 text-xl leading-relaxed sm:text-[1.65rem]">“{t.quote}”</blockquote>
                <div className="mt-7 flex flex-wrap items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <span className="relative h-12 w-12 overflow-hidden rounded-full ring-2 ring-gold-400">
                      <PhotoFallback src={t.photo ?? portrait(t.name)} name={t.name} sizes="48px" textClassName="text-sm" />
                    </span>
                    <div>
                      <div className="text-sm font-bold">{t.name}</div>
                      <div className="he-soft text-xs">{t.role}{t.company ? ` · ${t.company}` : ""}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex gap-0.5">
                      {Array.from({ length: t.rating }).map((_, k) => (
                        <Star key={k} className="h-4 w-4 fill-gold-400 text-gold-400" />
                      ))}
                    </div>
                    <div className="he-soft flex gap-2">
                      <button
                        type="button"
                        onClick={() => setI((v) => (v - 1 + N) % N)}
                        aria-label="Previous testimonial"
                        className="he-chip !p-2"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setI((v) => (v + 1) % N)}
                        aria-label="Next testimonial"
                        className="he-chip !p-2"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
                {t.course && (
                  <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-[var(--he-line)] pt-4">
                    <p className="he-brand-text text-xs font-bold uppercase tracking-[0.14em]">{t.course}</p>
                  </div>
                )}
            </div>
          </div>

          {/* selector - active learner + the next two, rolling up in a loop */}
          <div className="order-3 overflow-hidden lg:order-2">
            <div key={i} className="slide-up-in grid gap-3">
                {visible.map((x) => {
                  const k = testimonials.indexOf(x);
                  const pic = x.photo ?? portrait(x.name);
                  return (
                    <button
                      key={x.id}
                      type="button"
                      onClick={() => setI(k)}
                      aria-pressed={k === i}
                      className={`he-card he-hover flex items-center gap-3 p-4 text-left ${k === i ? "!border-brand-600" : ""}`}
                    >
                      <span className="relative flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-[var(--he-tint,#f6efe6)] text-sm font-bold">
                        <PhotoFallback src={pic} name={x.name} sizes="40px" textClassName="text-sm" />
                      </span>
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-bold">{x.name}</span>
                        <span className="he-soft block truncate text-xs">{x.role}</span>
                      </span>
                    </button>
                  );
                })}
            </div>
          </div>

          {/* every video testimonial, auto-scrolling; follows the quote above when it has a video.
              On mobile it sits between the quote and the learner selector; on desktop it spans the row below both. */}
          <div className="order-2 min-w-0 lg:order-3 lg:col-span-2">
            <H9VoiceVideos testimonials={testimonials} activeId={t.id} onHover={setPaused} />
          </div>
        </div>
      </div>
    </section>
  );
}
